import { Box, CircularProgress, TextField, useTheme } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import PrimaryButton from "src/theme/PrimaryButton/PrimaryButton";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import PasswordInput from "src/theme/PasswordInput/PasswordInput";
import { outlinedNoBorderSx } from "src/auth/pages/authInputStyles";

const uniqueId = "6cf1a0fa-8d75-4342-bf6b-1203d5b114d7";

export const DATA_TEST_ID = {
  FORM: `register-form-${uniqueId}`,
  REGISTRATION_CODE_INPUT: `register-registration-code-input-${uniqueId}`,
  USERNAME_INPUT: `register-username-input-${uniqueId}`,
  EMAIL_INPUT: `register-email-input-${uniqueId}`,
  PASSWORD_INPUT: `register-password-input-${uniqueId}`,
  CONFIRM_PASSWORD_INPUT: `register-confirm-password-input-${uniqueId}`,
  REGISTER_BUTTON: `register-button-${uniqueId}`,
  REGISTER_BUTTON_CIRCULAR_PROGRESS: `register-button-circular-progress-${uniqueId}`,
};

export interface RegisterFormProps {
  disabled?: boolean;
  notifyOnRegister: (email: string, password: string) => void;
  isRegistering: boolean;
}

const RegisterWithEmailForm: React.FC<Readonly<RegisterFormProps>> = ({
  disabled = false,
  notifyOnRegister,
  isRegistering,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const handleEmailChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setConfirmPassword(event.target.value);
  };

  const passwordsDoNotMatch = Boolean(confirmPassword) && password !== confirmPassword;

  const handleRegister = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isPasswordValid && !passwordsDoNotMatch && confirmPassword) {
      notifyOnRegister(email, password);
    }
  };

  const confirmPasswordFieldSx: SxProps<Theme> = {
    ...outlinedNoBorderSx,
    "& .MuiFormHelperText-root": {
      color: theme.palette.common.white,
      fontWeight: 700,
    },
    "& .MuiFormHelperText-root.Mui-error": {
      color: theme.palette.common.white,
      fontWeight: 700,
    },
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      width={"100%"}
      justifyContent={"space-evenly"}
      gap={theme.fixedSpacing(theme.tabiyaSpacing.sm)}
      component="form"
      onSubmit={handleRegister}
      data-testid={DATA_TEST_ID.FORM}
    >
      <TextField
        fullWidth
        placeholder={t("auth.pages.register.emailPlaceholder")}
        type="email"
        disabled={isRegistering || disabled}
        variant="outlined"
        required
        sx={outlinedNoBorderSx}
        onChange={(e) => handleEmailChange(e)}
        inputProps={{ "data-testid": DATA_TEST_ID.EMAIL_INPUT }}
      />
      <PasswordInput
        fullWidth
        placeholder={t("auth.pages.register.passwordPlaceholder")}
        disabled={isRegistering || disabled}
        variant="outlined"
        required
        onChange={(e) => handlePasswordChange(e)}
        value={password}
        onValidityChange={setIsPasswordValid}
        inputProps={{ "data-testid": DATA_TEST_ID.PASSWORD_INPUT }}
        sx={outlinedNoBorderSx}
      />
      <PasswordInput
        fullWidth
        placeholder={t("auth.pages.register.confirmPasswordPlaceholder")}
        disabled={isRegistering || disabled}
        variant="outlined"
        required
        shouldValidatePassword={false}
        onChange={(e) => handleConfirmPasswordChange(e)}
        value={confirmPassword}
        error={passwordsDoNotMatch}
        helperText={passwordsDoNotMatch ? t("common.validation.passwordsDoNotMatch") : ""}
        inputProps={{ "data-testid": DATA_TEST_ID.CONFIRM_PASSWORD_INPUT }}
        sx={confirmPasswordFieldSx}
      />
      <PrimaryButton
        variant="contained"
        color="secondary"
        showCircle
        style={{ marginTop: theme.fixedSpacing(3.5) }}
        type="submit"
        disabled={isRegistering || disabled || !isPasswordValid || !email || !confirmPassword || passwordsDoNotMatch}
        disableWhenOffline={true}
        data-testid={DATA_TEST_ID.REGISTER_BUTTON}
        sx={{
          backgroundColor: theme.palette.tertiary.light,
          color: theme.palette.secondary.main,
          alignSelf: "center",
          width: { xs: "90%", sm: "80%" },
          maxWidth: 420,
          fontSize: theme.typography.subtitle1.fontSize,
          alignItems: "center",
          "& > span:first-of-type": {
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 0,
          },
          "&.Mui-disabled": {
            opacity: 0.4,
            color: theme.palette.secondary.main,
            WebkitTextFillColor: theme.palette.secondary.main,
            backgroundColor: theme.palette.tertiary.light,
            "& > .MuiBox-root": {
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.tertiary.light,
            },
          },
        }}
      >
        {isRegistering ? (
          <span>
            <CircularProgress
              color={"secondary"}
              aria-label={t("auth.pages.register.components.registerWithEmailForm.registeringAria")}
              data-testid={DATA_TEST_ID.REGISTER_BUTTON_CIRCULAR_PROGRESS}
              size={16}
            />
          </span>
        ) : (
          <span>{t("common.buttons.register")}</span>
        )}
      </PrimaryButton>
    </Box>
  );
};

export default RegisterWithEmailForm;
